import { decodedObjectsFromPolyline, encodeObjectsToPolyline } from './polyline_util';
import { StringConstantsMixin } from '../application/javascripts/react_components/mixins/shared/string_constants_mixin';

const reverseCoord = coord => {
  return [coord[1], coord[0]];
};

const lineOrSegSort = (a, b) => {
  if (a.sequence_num && b.sequence_num) return a.sequence_num - b.sequence_num;
  if (a.sequenceNum && b.sequenceNum) return a.sequenceNum - b.sequenceNum;
  return a.id - b.id;
};

const getLineColor = atLine => {
  const defaultLineColor = '#ff0000';
  if (!atLine) {
    return defaultLineColor;
  }
  return atLine.lineDisplayProperty ? atLine.lineDisplayProperty.color : defaultLineColor;
};

const getLines = atMap => {
  if (!atMap) {
    return [];
  }
  const { routes, tracks } = atMap;
  const lines = routes && routes.length > 0 ? routes : tracks;
  if (!lines) {
    return [];
  }
  return lines.sort(lineOrSegSort);
};

const getSegments = atLine => {
  if (!atLine) {
    return [];
  }
  const { lineSegments, lineTimedSegments } = atLine;
  const segments = lineSegments && lineSegments.length > 0 ? lineSegments : lineTimedSegments;
  if (!segments) {
    return [];
  }
  return segments.sort(lineOrSegSort);
};

const decodeLatLngs = pointsData => {
  return decodedObjectsFromPolyline(pointsData, [5, 5]);
};

const decodeLngLats = pointsData => {
  return decodeLatLngs(pointsData).map(pt => reverseCoord(pt));
};

const encodeLatLngs = latLngs => {
  return encodeObjectsToPolyline(latLngs, 2, false, [5, 5]);
};

const decodeIdxElev = idxElevData => {
  return decodedObjectsFromPolyline(idxElevData, [2, 5]);
};

const decodeNDim = ndim => {
  return decodedObjectsFromPolyline(ndim, [5, 5, 5, 2, 5, 5]);
};

// Convert array of n-dim encoded polylines to standard (2-dim) polylines
const convertNDimTo2DimPolylines = nDimPolylines => {
  return nDimPolylines.map(nDimPolyline => {
    const latLngs = decodeNDim(nDimPolyline).map(point => [point[0], point[1]]);
    return encodeLatLngs(latLngs);
  });
};

const decodeSegmentLatLng = segment => {
  if (!segment || !segment.polyline) {
    return [];
  }
  const pointsData = segment.polyline.pointsData;
  if (!pointsData || pointsData === '') {
    return [];
  }
  return decodeLatLngs(pointsData);
};

const decodeSegmentIdxElev = segment => {
  if (!segment || !segment.polyline) {
    return [];
  }
  const idxElevData = segment.polyline.indexedElevationData;
  if (!idxElevData) {
    return [];
  }
  return decodeIdxElev(idxElevData);
};

const decodeSegmentIdxElevHash = segment => {
  const hash = {};
  decodeSegmentIdxElev(segment).forEach(pair => {
    hash[pair[0]] = pair[1];
  });
  return hash;
};

const demPtToNdim = pt => {
  return [pt.location.latitude, pt.location.longitude, pt.elevation, 0, 0, 0];
};

const decodeSegmentLngLat = segment => {
  return decodeSegmentLatLng(segment).map(latLng => reverseCoord(latLng));
};

const encodeSegmentLatLng = (latLngs, sequenceNum) => {
  return {
    polyline: {
      pointsData: encodeLatLngs(latLngs)
    },
    sequence_num: sequenceNum
  };
};

const encodeSegmentLngLat = (lngLats, sequenceNum) => {
  return encodeSegmentLatLng(
    lngLats.map(lngLat => reverseCoord(lngLat)),
    sequenceNum
  );
};

const buildAtMap = data => {
  return {
    name: StringConstantsMixin.strings.MAP,
    source: 1033,
    presentationType: 'M',
    ...data // Destructing should override any default params above
  };
};

const lngLatBoundsToAtBounds = lngLatBounds => {
  return {
    latitudeBottomRight: lngLatBounds.getSouth(),
    latitudeTopLeft: lngLatBounds.getNorth(),
    longitudeBottomRight: lngLatBounds.getEast(),
    longitudeTopLeft: lngLatBounds.getWest()
  };
};

const atBoundsToLngLatBounds = atBounds => {
  const { longitudeTopLeft, latitudeTopLeft, longitudeBottomRight, latitudeBottomRight } = atBounds;
  const sw = [longitudeTopLeft, latitudeBottomRight];
  const ne = [longitudeBottomRight, latitudeTopLeft];
  return [sw, ne];
};

const lngLatToAtLocation = lngLat => {
  const { lat, lng } = lngLat;
  return {
    latitude: lat,
    longitude: lng
  };
};

const lngLatBoundsToAlgoliaBounds = map => {
  const bounds = map.getBounds();
  const boundingBox = [bounds.getNorth(), bounds.getWest(), bounds.getSouth(), bounds.getEast()].join(',');
  return boundingBox;
};

const getMapDistance = map => {
  let distance = 0.0;
  distance += map.routes
    .map(route => {
      if (route.lineGeoStats) {
        const segDistance = parseFloat(route.lineGeoStats.distanceTotal);
        return Number.isNaN(segDistance) ? 0.0 : segDistance;
      }
      return 0.0;
    })
    .reduce((a, b) => a + b, 0);
  distance += map.tracks
    .map(track => {
      if (track.lineTimedGeoStats) {
        const segDistance = parseFloat(track.lineTimedGeoStats.distanceTotal);
        return Number.isNaN(segDistance) ? 0.0 : segDistance;
      }
      return 0.0;
    })
    .reduce((a, b) => a + b, 0);
  return distance;
};

const isMapFaulty = map => {
  if (!map) {
    return true;
  }

  if (map.routes) {
    const missingLineSegments = map.routes.some(route => {
      return !route.lineSegments || route.lineSegments.length < 1;
    });

    if (missingLineSegments) {
      return true;
    }
  }
  return false;
};

const getLineCenterCoordinates = route => {
  const coordinates = route.lineSegments.map(segment => decodeSegmentLngLat(segment)).flat();
  if (coordinates.length === 0) {
    return coordinates;
  }

  return coordinates[Math.round(coordinates.length / 2)];
};

export {
  atBoundsToLngLatBounds,
  buildAtMap,
  convertNDimTo2DimPolylines,
  decodeNDim,
  decodeLatLngs,
  decodeLngLats,
  decodeSegmentIdxElev,
  decodeSegmentIdxElevHash,
  decodeSegmentLatLng,
  decodeSegmentLngLat,
  demPtToNdim,
  encodeLatLngs,
  encodeSegmentLngLat,
  getLineCenterCoordinates,
  getLineColor,
  getLines,
  getMapDistance,
  getSegments,
  isMapFaulty,
  lineOrSegSort,
  lngLatBoundsToAlgoliaBounds,
  lngLatBoundsToAtBounds,
  lngLatToAtLocation,
  reverseCoord
};
